import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { DEVICE_CATEGORY, categoryForFps } from '../../../services/device-performance/category';

// Both ends have to keep up for the feed to read as smooth, so the health chip
// judges the slower of the two rates rather than either one on its own, on the
// same FPS bands as the device-performance categories.
const FPS_HEALTH = {
  [DEVICE_CATEGORY.SMOOTH]: { key: 'stable', color: '#4ADE80' },
  [DEVICE_CATEGORY.LIMITED]: { key: 'fair', color: '#FBBF24' },
  [DEVICE_CATEGORY.NOT_SUPPORTED]: { key: 'low', color: '#F87171' },
};

const resolveFpsHealth = values => {
  const known = values.filter(value => typeof value === 'number');
  const category = known.length ? categoryForFps(Math.min(...known)) : null;
  return FPS_HEALTH[category ?? DEVICE_CATEGORY.NOT_SUPPORTED];
};

// `lg` (1024px) matches screenSizes.lg — the same phone/desktop line the rest
// of the exercise page's layout branches on — so this stays in step with
// whatever pill/overlay sizing those already assume.
const Divider = () => <span tw="h-4 w-px shrink-0 bg-white/20 lg:h-6" />;

// Backend and frontend rates read as one row, so the side is carried by the
// label's colour rather than by a second line of text.
const FpsReading = ({ label, value, color }) => (
  <span tw="flex items-baseline gap-1 lg:gap-2">
    <span
      tw="text-[10px] font-[700] uppercase tracking-[0.06em] lg:text-[13px] lg:tracking-[0.08em]"
      style={{ color }}
    >
      {label}
    </span>
    {/* Big enough to read from across the room on desktop; a phone is held (or
        propped) closer, so it scales down under the lg breakpoint instead of
        crowding out the rest of the pill on a narrow screen.
        tabular-nums so a rate flipping 29/30/31 doesn't resize the pill. */}
    <span tw="text-[20px] font-[800] leading-none tabular-nums lg:text-[28px]">{value ?? '-'}</span>
  </span>
);

// Dev-only debug HUD: a compact FPS pill at the top centre of the viewport. It
// clears the rep counter / quality column (top start) and the settings bar
// (top end), and sits above the TIMED_REPS countdown — which drops below it
// instead (see performance-readout-overlay).
const ScoreAndFpsOverlay = ({ currentFps, frontendFps }) => {
  const { t } = useTranslation('exercise');
  const health = resolveFpsHealth([currentFps, frontendFps]);

  return (
    <>
      {(currentFps !== null || frontendFps !== null) && (
        <div tw="pointer-events-none fixed inset-x-0 top-4 z-[250] flex justify-center px-4">
          <div tw="flex items-center gap-2 rounded-[12px] border border-white/15 bg-black/70 px-3 py-2 text-white shadow-[0_10px_28px_rgba(0,0,0,0.35)] backdrop-blur-[6px] lg:gap-4 lg:rounded-[14px] lg:px-5 lg:py-3.5">
            {/* Signal-bar glyph drawn inline — no asset, and it scales with the
                pill's own type rather than a fixed-size SVG. Dropped below lg:
                on a narrow phone it's decorative weight the row can't spare, and
                the health dot at the other end already carries that signal. */}
            <span tw="hidden h-[18px] items-end gap-[2px] lg:flex" aria-hidden="true">
              <span tw="h-[8px] w-[4px] rounded-[1px] bg-white/45" />
              <span tw="h-[13px] w-[4px] rounded-[1px] bg-white/65" />
              <span tw="h-[18px] w-[4px] rounded-[1px] bg-white/85" />
            </span>
            <span tw="text-[10px] font-[700] uppercase tracking-[0.1em] text-white/60 lg:text-[13px] lg:tracking-[0.14em]">
              {t('debugOverlay.fps')}
            </span>
            <Divider />
            <FpsReading
              label={t('debugOverlay.backendSuffix')}
              value={currentFps}
              color="#4ADE80"
            />
            <Divider />
            <FpsReading
              label={t('debugOverlay.frontendSuffix')}
              value={frontendFps}
              color="#60A5FA"
            />
            <Divider />
            <span tw="flex items-center gap-1 lg:gap-1.5">
              <span
                tw="h-1.5 w-1.5 shrink-0 rounded-full lg:h-2 lg:w-2"
                style={{ backgroundColor: health.color, boxShadow: `0 0 0 3px ${health.color}33` }}
              />
              <span tw="text-[10px] font-[600] text-white/80 lg:text-[12px]">
                {t(`debugOverlay.status.${health.key}`)}
              </span>
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default ScoreAndFpsOverlay;
